import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import { visionService } from "@/services/vision-service";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import type { RoomTrackingContext } from "@/features/vision-360/ai-mode/utils/roomTrackingContext";
import { currentDevice } from "@/features/vision-360/ai-mode/utils/proposalSharedContext";
import { useProposalT } from "../i18n/ProposalI18nContext";
import "../proposal.css";

interface Message {
  role: "user" | "assistant" | "divider";
  content: string;
}

interface ChatPanelProps {
  proposalId: string;
  dealId?: string;
  userEmail?: string;
  clientSegment?: string;
  suppressTracking?: boolean;
  /** Also the source of the tracked room_id — the room identity has a single
   * prop so payload keys can never disagree. */
  roomTrackingContext?: RoomTrackingContext;
  spaceName?: string;
  spaceAddress?: string;
  spaceCapacity?: number;
  pricePerPosition?: number;
}

// Splits content by GFM table blocks so they can be rendered natively without remark-gfm
type Segment =
  | { type: "markdown"; text: string }
  | { type: "table"; text: string };

function segmentMarkdown(content: string): Segment[] {
  const segments: Segment[] = [];
  const blocks = content.split(/\n{2,}/);
  const mdBuffer: string[] = [];

  for (const block of blocks) {
    const lines = block.split("\n").filter((l) => l.trim());
    const isTable =
      lines.length >= 3 &&
      lines.every((l) => l.trim().startsWith("|")) &&
      /^\|[-|: ]+\|$/.test(lines[1].trim());

    if (isTable) {
      if (mdBuffer.length) {
        segments.push({ type: "markdown", text: mdBuffer.join("\n\n") });
        mdBuffer.length = 0;
      }
      segments.push({ type: "table", text: block });
    } else {
      mdBuffer.push(block);
    }
  }

  if (mdBuffer.length)
    segments.push({ type: "markdown", text: mdBuffer.join("\n\n") });
  return segments;
}

function renderCellContent(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} style={{ fontWeight: 600 }}>
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

function renderTableBlock(block: string, key: number): React.ReactNode {
  const lines = block.split("\n").filter((l) => l.trim());
  const headers = lines[0]
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());
  const rows = lines.slice(2).map((l) =>
    l
      .replace(/^\||\|$/g, "")
      .split("|")
      .map((c) => c.trim()),
  );

  return (
    <div key={key} className="chat-table-wrap">
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          margin: "8px 0",
          fontSize: "13px",
        }}
      >
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  padding: "6px 8px",
                  borderBottom: "2px solid #D1CEC9",
                  textAlign: "left",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {renderCellContent(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: "1px solid #E8E6E3" }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: "6px 8px" }}>
                  {renderCellContent(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const markdownComponents: Components = {
  p: ({ children }) => <p style={{ margin: "0 0 8px" }}>{children}</p>,
  h1: ({ children }) => (
    <p style={{ fontWeight: 700, fontSize: "14px", margin: "12px 0 4px" }}>
      {children}
    </p>
  ),
  h2: ({ children }) => (
    <p style={{ fontWeight: 700, fontSize: "14px", margin: "12px 0 4px" }}>
      {children}
    </p>
  ),
  h3: ({ children }) => (
    <p style={{ fontWeight: 700, fontSize: "13px", margin: "12px 0 4px" }}>
      {children}
    </p>
  ),
  h4: ({ children }) => (
    <p style={{ fontWeight: 600, fontSize: "13px", margin: "10px 0 4px" }}>
      {children}
    </p>
  ),
  h5: ({ children }) => (
    <p style={{ fontWeight: 600, fontSize: "13px", margin: "8px 0 4px" }}>
      {children}
    </p>
  ),
  h6: ({ children }) => (
    <p style={{ fontWeight: 600, fontSize: "12px", margin: "8px 0 4px" }}>
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul style={{ margin: "4px 0 8px", paddingLeft: "16px" }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: "4px 0 8px", paddingLeft: "16px" }}>{children}</ol>
  ),
  li: ({ children }) => <li style={{ marginBottom: "2px" }}>{children}</li>,
  hr: () => (
    <hr
      style={{
        border: "none",
        borderTop: "1px solid #D1CEC9",
        margin: "10px 0",
      }}
    />
  ),
  blockquote: ({ children }) => (
    <blockquote
      style={{
        borderLeft: "3px solid #FFD900",
        paddingLeft: "10px",
        margin: "8px 0",
        color: "#78736D",
      }}
    >
      {children}
    </blockquote>
  ),
  strong: ({ children }) => (
    <strong style={{ fontWeight: 600 }}>{children}</strong>
  ),
  code: ({ children }) => (
    <code
      style={{
        fontFamily: "monospace",
        background: "#E8E6E3",
        padding: "1px 4px",
        borderRadius: "3px",
        fontSize: "12px",
      }}
    >
      {children}
    </code>
  ),
  a: ({ href, children }) => {
    const safeHref = href && /^https?:\/\//i.test(href) ? href : undefined;
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1A1814", fontWeight: 600 }}
      >
        {children}
      </a>
    );
  },
};

async function pollUntilDone(
  jobId: string,
  conversationId: string,
): Promise<string> {
  let done = false;
  let answer = "";
  let attempts = 0;
  const MAX_ATTEMPTS = 60;
  while (!done && attempts < MAX_ATTEMPTS) {
    const data = await visionService.chatPoll(jobId, conversationId);
    done = data.done;
    answer = data.answer ?? "";
    if (!done) {
      await new Promise<void>((resolve) => setTimeout(resolve, 1500));
      attempts++;
    }
  }
  if (!done) throw new Error("Response timeout after 90s");
  return answer;
}

export function ChatPanel({
  proposalId,
  dealId,
  clientSegment,
  suppressTracking = false,
  roomTrackingContext,
  userEmail,
  spaceName,
  spaceAddress,
  spaceCapacity,
  pricePerPosition,
}: ChatPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevSpaceNameRef = useRef<string | undefined>(undefined);
  const { t, locale } = useProposalT();

  const quickChips = [
    { key: "monthlyValue", text: t("chat.chip.monthlyValue"), icon: "💰" },
    { key: "parking", text: t("chat.chip.parking"), icon: "🅿️" },
    { key: "internet", text: t("chat.chip.internet"), icon: "📶" },
    { key: "meetingRooms", text: t("chat.chip.meetingRooms"), icon: "🤝" },
    { key: "credits", text: t("chat.chip.credits"), icon: "🎫" },
    { key: "contract", text: t("chat.chip.contract"), icon: "📄" },
    { key: "scheduleVisit", text: t("chat.chip.scheduleVisit"), icon: "📅" },
  ];

  // Initial welcome + reset on space change
  useEffect(() => {
    if (spaceName === prevSpaceNameRef.current) return;
    const prev = prevSpaceNameRef.current;
    prevSpaceNameRef.current = spaceName;

    const lines: string[] = [
      t("chat.welcomeLine1", {
        spaceName: spaceName ?? t("chat.defaultSpaceName"),
      }),
    ];
    if (spaceAddress || spaceCapacity || pricePerPosition) {
      const details: string[] = [];
      if (spaceAddress)
        details.push(t("chat.welcomeAt", { address: spaceAddress }));
      if (spaceCapacity)
        details.push(t("chat.welcomeCapacity", { capacity: spaceCapacity }));
      if (pricePerPosition)
        details.push(
          t("chat.welcomePrice", {
            price: pricePerPosition.toLocaleString("pt-BR"),
          }),
        );
      lines.push(`${t("chat.welcomeDetailsPrefix")} ${details.join(", ")}.`);
    }
    lines.push(t("chat.welcomeAskAnything"));
    const welcome: Message = { role: "assistant", content: lines.join("\n\n") };

    setMessages((msgs) => {
      if (msgs.length === 0 || prev === undefined) return [welcome];
      const divider: Message = { role: "divider", content: spaceName ?? "" };
      return [...msgs, divider, welcome];
    });
    setConversationId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceName]);

  useEffect(() => {
    if (messages.length === 0) return;
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [messages, isLoading]);

  async function sendMessage(text: string, isChip = false) {
    if (!text.trim() || isLoading) return;

    if (!suppressTracking) {
      void sendTrackDataServer({
        eventName: "VISION_PROPOSAL_CHAT_MESSAGE_SENT",
        productType: "PO",
        url: window.location.pathname,
        data: {
          proposal_id: proposalId,
          deal_id: dealId ?? null,
          email: userEmail ?? null,
          proposal_uuid: proposalId,
          device: currentDevice(),
          client_segment: clientSegment || null,
          ...roomTrackingContext,
          room_id: roomTrackingContext?.espaco_id ?? "",
          room_name: spaceName ?? null,
          message_length: text.trim().length,
          is_chip: isChip,
        },
      });
    }

    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setIsLoading(true);

    try {
      const contextualMessage = spaceName
        ? `[Sobre: ${spaceName}] [Language: ${locale}] ${text}`
        : `[Language: ${locale}] ${text}`;

      const body: Record<string, string> = { message: contextualMessage };
      if (conversationId) {
        body.conversation_id = conversationId;
      } else {
        body.proposal_uuid = proposalId;
        if (dealId) body.deal_id = dealId;
        if (userEmail) body.user_email = userEmail;
      }

      const { conversation_id, job_id } = await visionService.chatSend(body);

      if (!conversationId) setConversationId(conversation_id);

      const answer = await pollUntilDone(job_id, conversation_id);
      setMessages((prev) => [...prev, { role: "assistant", content: answer }]);
    } catch (err) {
      console.error("[ChatPanel] sendMessage error:", err);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: t("chat.errorMessage"),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  const chatMessages = messages.filter((m) => m.role !== "divider");
  const hasUserMessages = chatMessages.some((m) => m.role === "user");

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "480px",
          maxHeight: "70vh",
          minHeight: "320px",
          background: "white",
          borderRadius: "12px",
          border: "1px solid #E8E6E3",
          overflow: "hidden",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        {/* Context badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 16px",
            background: "#F5F3EF",
            borderBottom: "1px solid #E8E6E3",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "#22C55E",
              flexShrink: 0,
              animation: "chat-pulse-dot 2s ease-in-out infinite",
            }}
          />
          <div>
            <div
              style={{
                fontSize: "12px",
                fontWeight: 500,
                color: "#78736D",
                letterSpacing: "0.02em",
              }}
            >
              {t("chat.contextLabel")}
            </div>
            {spaceName && (
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#1A1814",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                {spaceName}
              </div>
            )}
          </div>
        </div>

        {/* Messages area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            scrollBehavior: "smooth",
          }}
        >
          {messages.map((msg, i) => {
            if (msg.role === "divider") {
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    margin: "4px 0",
                  }}
                >
                  <div
                    style={{ flex: 1, height: "1px", background: "#E8E6E3" }}
                  />
                  <span
                    style={{
                      fontSize: "10px",
                      fontWeight: 600,
                      color: "#9E9A94",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("chat.dividerNowAbout", { spaceName: msg.content })}
                  </span>
                  <div
                    style={{ flex: 1, height: "1px", background: "#E8E6E3" }}
                  />
                </div>
              );
            }

            return (
              <div
                key={i}
                className={`chat-msg max-w-[92%] sm:max-w-[85%]${msg.role === "assistant" ? " chat-msg-assistant" : ""}`}
                style={{
                  padding: "10px 14px",
                  borderRadius: "16px",
                  fontSize: "14px",
                  lineHeight: 1.5,
                  wordBreak: "break-word",
                  alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                  background: msg.role === "user" ? "#FFD900" : "#F5F3EF",
                  color: "#1A1814",
                  borderBottomRightRadius: msg.role === "user" ? "4px" : "16px",
                  borderBottomLeftRadius: msg.role === "user" ? "16px" : "4px",
                  fontWeight: msg.role === "user" ? 500 : 400,
                }}
              >
                {msg.role === "user"
                  ? msg.content
                  : segmentMarkdown(msg.content).map((seg, si) =>
                      seg.type === "table" ? (
                        renderTableBlock(seg.text, si)
                      ) : (
                        <ReactMarkdown key={si} components={markdownComponents}>
                          {seg.text}
                        </ReactMarkdown>
                      ),
                    )}
              </div>
            );
          })}

          {isLoading && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "10px 14px",
                background: "#F5F3EF",
                borderRadius: "16px",
                borderBottomLeftRadius: "4px",
                alignSelf: "flex-start",
                animation: "chat-fade-in 0.2s ease",
              }}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    display: "inline-block",
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: "#B8B3AD",
                    animation: `chat-typing-bounce 1.4s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggestion chips — shown until user sends first message */}
        {!hasUserMessages && (
          <div
            className="chat-chips-row"
            style={{ padding: "0 16px 12px", flexShrink: 0 }}
          >
            {quickChips.map((chip) => (
              <button
                key={chip.text}
                className="chat-chip-btn"
                onClick={() => {
                  if (chip.key === "scheduleVisit" && !suppressTracking) {
                    void sendTrackDataServer({
                      eventName: "VISION_PROPOSAL_SCHEDULE_VISIT_CLICKED",
                      productType: "PO",
                      url: window.location.pathname,
                      data: {
                        proposal_id: proposalId,
                        deal_id: dealId ?? null,
                        email: userEmail ?? null,
                        proposal_uuid: proposalId,
                        device: currentDevice(),
                        client_segment: clientSegment || null,
                        ...roomTrackingContext,
                        room_id: roomTrackingContext?.espaco_id ?? "",
                        room_name: spaceName ?? null,
                      },
                    });
                  }
                  void sendMessage(chip.text, true);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "6px 12px",
                  borderRadius: "999px",
                  border: "1px solid #E8E6E3",
                  background: "white",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#1A1814",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                <span>{chip.icon}</span>
                {chip.text}
              </button>
            ))}
          </div>
        )}

        {/* Input area */}
        <div
          className="px-3 sm:px-4"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            paddingTop: "10px",
            paddingBottom: "10px",
            borderTop: "1px solid #E8E6E3",
            background: "#FAFAF8",
            flexShrink: 0,
          }}
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void sendMessage(input);
              }
            }}
            placeholder={t("chat.placeholder")}
            disabled={isLoading}
            className="chat-input"
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: "44px",
              padding: "10px 14px",
              borderRadius: "999px",
              border: "1px solid #E8E6E3",
              background: "white",
              fontFamily: "'Inter', sans-serif",
              fontSize: "14px",
              color: "#1A1814",
              outline: "none",
              transition: "border-color 0.2s",
            }}
          />
          <button
            className="chat-send-btn"
            onClick={() => void sendMessage(input)}
            disabled={!input.trim() || isLoading}
            style={{
              width: "44px",
              height: "44px",
              minWidth: "44px",
              borderRadius: "50%",
              border: "none",
              background: "#FFD900",
              color: "#1A1814",
              cursor: input.trim() && !isLoading ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
              flexShrink: 0,
              opacity: !input.trim() || isLoading ? 0.5 : 1,
            }}
            aria-label={t("chat.sendAriaLabel")}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}
